import { OutputData } from '@editorjs/editorjs';
import { PublicSettingsType, PublicityType } from '@pages/api/types';
import { BadgeColorOptions } from '@utils/badges';

export type Category = {
  id: string;
  slug: string;
  name: string;
  title_seo?: string;
  description_seo?: string;
  keywords_seo?: string;
  color?: BadgeColorOptions;
  news?: News[];
  order: number;
  deleted_at?: Date | null;
};
export type CategoryFormProps = Omit<Category, 'slug'> & {
  slug?: string;
  reactivate?: boolean;
};

export type Author = {
  id?: string;
  fk_image_id?: string;
  email: string;
  name: string;
  slug?: string;
  image?: Image;
  birthday?: Date | string;
  description: string;
  created_at?: Date | string;
  modified_at?: Date | string;
  deleted_at?: Date | string;
};

export type AuthorFormProps = Omit<Author, 'birthday'> & {
  birthday?: Date | undefined;
  file: File | null;
  imageKey: string;
  reactivate?: boolean;
};

export type Image = {
  id: string;
  key: string;
};

export type News = {
  id: string;
  fk_user_id?: string;
  fk_author_id?: string;
  fk_category_id?: string;
  fk_image_id?: string;
  fk_mobile_image_id?: string;
  spotify_url?: string;
  fk_approved_by_user_id?: string;
  title: string;
  title_seo?: string;
  description_seo?: string;
  keywords_seo?: string;
  sub_title: string;
  description_text: {
    id: string;
    text: string;
  };
  description: OutputData;
  header_image_key?: string;
  mobile_image_key?: string;
  slug: string;
  is_highlighted: boolean;
  syndicate?: boolean;
  publish_at: string;
  approved_at: string;
  author: Author;
  category: Category;
  manual_notification: ManualNotification;
  created_at: string;
  header_image?: Image;
  mobile_image?: Image;
  modified_at: string;
  rejected_at: string;
};

export type NewsDescription = {
  id: string;
  text: string;
  created_at: Date | string;
};

export type NewsFilters = {
  publish_at?: string;
  author?: string;
  category?: string;
  is_highlighted?: boolean;
  is_approved?: boolean;
  is_rejected?: boolean;
  is_published?: boolean;
  page?: number;
  limit?: number;
};

export type PublicNewsFilters = {
  publish_at?: string;
  author?: string;
  category?: string;
  page?: number;
  limit?: number;
};

export type PublicNews = Omit<
  News,
  'is_highlighted' | 'modified_at' | 'rejected_at'
>;

export type CreateNewsForm = {
  id?: string;
  author_id: string;
  category_id: string;
  title: string;
  title_seo: string;
  description_seo: string;
  keywords_seo: string;
  sub_title: string;
  description?: OutputData;
  header_image_key?: string;
  mobile_image_key?: string;
  slug?: string;
  is_highlighted?: boolean;
  send_notification?: boolean;
  syndicate?: boolean;
  publish_at?: Date | string;
  file?: File | null;
  file_mobile?: File | null;
  editorJsData?: OutputData;
  notification_title?: string;
  notification_body?: string;
  spotify_url?: string;
};

export type ApiListResponse<T> = {
  data: T;
  meta: {
    current_page: number;
    total_pages: number;
    total_records: number;
    page_size: number;
  };
};

export type CreateAuthorForm = {
  email: string;
  name: string;
  birthday: Date;
  description: string;
};

export type LiveBlog = {
  id: string;
  fk_user_id?: string;
  fk_author_id?: string;
  fk_category_id?: string;
  fk_image_id?: string;
  description_seo?: string;
  title_seo?: string;
  keywords_seo?: string;
  fk_approved_by_user_id?: string;
  title: string;
  description: string;
  header_image_id?: string;
  slug: string;
  is_highlighted: boolean;
  publish_at: string;
  approved_at: string;
  author: Author;
  category: Category;
  created_at: string;
  stopped_at?: string;
  header_image?: Image;
  modified_at: string;
  rejected_at: string;
  manual_notification?: ManualNotification;
};

export type LiveBlogFilters = {
  page?: number;
  limit?: number;
};

export type PublicLiveBlogFilters = {
  page?: number;
  limit?: number;
  fetchAll?: boolean;
};

export type CreateLiveBlogForm = {
  id?: string;
  author_id: string;
  category_id: string;
  title?: string;
  title_seo?: string;
  description_seo?: string;
  keywords_seo?: string;
  description?: string;
  header_image_id?: string;
  slug?: string;
  is_highlighted?: boolean;
  send_notification?: boolean;
  publish_at?: Date | string;
  file?: File | null;
  notification_title?: string;
  notification_body?: string;
};

export type LivePost = {
  id: string;
  fk_user_id?: string;
  fk_author_id?: string;
  fk_live_blog_id: string;
  fk_approved_by_user_id?: string;
  title: string;
  description: OutputData;
  slug: string;
  author: Author;
  created_at: string;
  approved_at?: string;
  modified_at: string;
  rejected_at?: string;
  send_notification?: boolean;
  manual_notification: ManualNotification;
};

export type LivePostFilters = {
  page?: number;
  limit?: number;
  slug: string;
};

export type CreateLivePostForm = {
  id?: string;
  author_id: string;
  title?: string;
  description?: OutputData;
  slug?: string;
  notification_body?: string;
  send_notification?: boolean;
  notification_title?: string;
};

export type PublicLivePost = {
  id: string;
  title: string;
  description: OutputData;
  slug: string;
  author: Author;
  created_at: string;
  approved_at?: string;
  rejected_at?: string;
};

export type PublicLiveBlog = {
  id: string;
  title: string;
  description: string;
  title_seo?: string;
  description_seo?: string;
  keywords_seo?: string;
  header_image_url?: string;
  slug: string;
  is_highlighted: boolean;
  publish_at: string;
  author: Author;
  category: Category;
  header_image?: Image;
  created_at: string;
};

export type Podcast = {
  id: string;
  fk_user_id?: string;
  fk_author_id?: string;
  fk_category_id?: string;
  fk_image_id?: string;
  title: string;
  title_seo?: string;
  description_seo?: string;
  keywords_seo?: string;
  description: string;
  slug: string;
  is_highlighted: boolean;
  is_video?: boolean;
  publish_at: string;
  author: Author;
  category: Category;
  header_image_key?: string;
  header_image?: Image;
  created_at: string;
  modified_at: string;
};

export type PublicPodcast = {
  id: string;
  title: string;
  description: string;
  slug: string;
  is_highlighted: boolean;
  is_video?: boolean;
  publish_at: string;
  author: Author;
  category: Category;
  header_image_key?: string;
  header_image?: Image;
};

export type CreatePodcastForm = {
  id?: string;
  author_id: string;
  header_image_key?: string;
  category_id: string;
  title?: string;
  title_seo?: string;
  description_seo?: string;
  keywords_seo?: string;
  description?: string;
  slug?: string;
  is_highlighted?: boolean;
  is_video?: boolean;
  publish_at?: Date | string;
  file?: File | null;
};

export type PodcastFilters = {
  page?: number;
  limit?: number;
  publish_at?: string;
  fetchAll?: boolean;
};

export type PublicityFilters = {
  page?: number;
  limit?: number;
  type?: PublicityType;
  approved_at?: boolean;
};

export type PublicPublicityFilters = {
  page?: number;
  limit?: number;
  fetchAll?: boolean;
};

export type Publicity = {
  id: string;
  fk_image_id?: string;
  fk_approved_by_user_id?: string;
  title: string;
  name: string;
  type: PublicityType;
  description: string;
  redirect_url?: string;
  click_count: number;
  view_count: number;
  image?: Image;
  start_date?: string;
  end_date?: string;
  approved_at: string;
  stopped_at?: string;
  created_at: string;
  deleted_at: string;
  modified_at: string;
  rejected_at: string;
};

export type CreatePublicityForm = {
  id?: string;
  image_key?: string;
  name?: string;
  redirect_url?: string;
  type?: PublicityType;
  title?: string;
  start_date: string | Date;
  end_date: string | Date;
  description?: string;
  file?: File | null;
};

export type PublicityPublic = {
  id: string;
  title: string;
  name: string;
  type: PublicityType;
  description: string;
  redirect_url?: string;
  view_count: number;
  image?: Image;
  start_date?: string;
  end_date?: string;
};

export type UserRole = 'ADMIN' | 'AUTHOR' | 'USER';

export const userRole = {
  ADMIN: 'ADMIN',
  USER: 'USER',
  AUTHOR: 'AUTHOR',
};

export type User = {
  id: string;
  fk_author_id?: string;
  author?: Author;
  role: UserRole;
  emailVerified: string;
  email: string;
  name: string;
  received_notifications?: InternalNotifications;
  sent_notifications?: InternalNotifications;
  created_at: string;
  deleted_at: string;
  modified_at: string;
};

export type CreateUserForm = {
  id?: string;
  name: string;
  role?: UserRole;
  email: string;
};

export type InternalNotifications = {
  id: string;
};

export type UsersFilters = {
  page?: number;
  limit?: number;
};

export type Episode = {
  id: string;
  fk_user_id?: string;
  fk_podcast_id?: string;
  title: string;
  description?: string;
  spotify_url: string;
  is_video?: boolean;
  approved_at: string;
  rejected_at: string;
  created_at: string;
  modified_at: string;
  podcast?: Podcast;
  manual_notification?: ManualNotification;
};

export type PublicEpisode = {
  id: string;
  title: string;
  description?: string;
  spotify_url: string;
  is_video?: boolean;
  podcast?: Podcast;
  approved_at?: string;
};

export type CreateEpisodeForm = {
  id?: string;
  title?: string;
  description?: string;
  spotify_url: string;
  podcastId: string;
  is_video?: boolean;
  send_notification?: boolean;
  notification_body?: string;
  notification_title?: string;
};

export type EpisodeFilters = {
  page?: number;
  limit?: number;
  slug: string;
  approved_at?: string;
  rejected_at?: string;
};

export type PublicEpisodeFilters = {
  page?: number;
  limit?: number;
  slug?: string;
  fetchAll?: boolean;
};

export type PublicSetting = {
  key: PublicSettingsType;
  value?: OutputData;
};

export type PublicSettingSEO = {
  key: PublicSettingsType;
  value: {
    name: SEOPagesName;
    title?: string;
    description?: string;
    keywords?: string;
  }[];
};

export type InternalNotificationType =
  | 'CREATED_NEWS'
  | 'CREATED_LIVE_BLOG'
  | 'CREATED_LIVE_POST'
  | 'CREATED_PUBLICITY'
  | 'CREATED_PODCAST'
  | 'CREATED_EPISODE'
  | 'APPROVED_NEWS'
  | 'APPROVED_EPISODE'
  | 'APPROVED_LIVE_BLOG'
  | 'APPROVED_LIVE_POST'
  | 'APPROVED_PUBLICITY'
  | 'REJECTED_NEWS'
  | 'REJECTED_EPISODE'
  | 'REJECTED_LIVE_BLOG'
  | 'REJECTED_LIVE_POST'
  | 'REJECTED_PUBLICITY'
  | 'STOPPED_PUBLICITY'
  | 'STOPPED_LIVE_BLOG';

export type InternalNotification = {
  id: string;
  fk_sender_id: string;
  fk_recipient_id: string;
  resource_id: string;
  resource_url: string;
  notification_type: InternalNotificationType;
  created_at: string;
  read_at: string;
  sender: User;
  recipient: User;
};

export type ManualNotification = {
  id?: string;
  title?: string;
  body?: string;
  send_at?: string | Date;
  sent?: boolean;
  url?: string;
};

export type ManualNotificationsFilters = {
  page?: number;
  limit?: number;
};

export type Search = {
  id: string;
  type: SearchQueryType;
  slug?: string;
  image_key?: string;
  name?: string;
  title?: string;
  subTitle: string;
  description?: string;
  category?: {
    title: string;
    slug: string;
  };
  created_at?: Date;
  publish_at?: Date;
};

export type SearchQueryType =
  | 'AUTHOR'
  | 'LIVEBLOG'
  | 'CATEGORY'
  | 'NEWS'
  | 'PODCAST';

// 'relevance' is a UI-only value: it maps to the *absence* of an `order` query
// param (relevance is the API's default ordering). See RAD-68 / ADR 0002.
export type OrderByFilter = 'desc' | 'asc' | 'relevance';

export type SearchFilters = {
  authors?: string[] | undefined;
  categories?: string[] | undefined;
  date?: string;
  page?: number;
  limit?: number;
  order?: OrderByFilter;
  searchQuery?: string;
  types?: SearchQueryType[];
};

export type SearchFiltersQuery = {
  authors?: string | undefined;
  categories?: string | undefined;
  date?: string;
  page?: number;
  limit?: number;
  orderBy?: OrderByFilter;
  searchQuery?: string;
  types?: string;
};

export type SearchFilterType =
  | 'author'
  | 'category'
  | 'date'
  | 'orderBy'
  | 'type';

export type FilterView = { value: SearchQueryType | string; name: string }[];

export type SelectedFilters = {
  type: SearchFilterType;
  value: string;
  name: string;
};

export type SearchFiltersArray = {
  value: SearchFilterType;
  translationKey: string;
}[];

export type AuthorsFilters = {
  searchQuery?: string;
  page?: number;
  limit?: number;
  fetchAll?: boolean;
};

export type NotificationsConfigurationsType =
  | 'default_notification_text_news'
  | 'default_notification_text_live_blog'
  | 'default_notification_text_live_post'
  | 'default_notification_text_episode';

export type CreateSettingsForm = {
  notifications: {
    id?: string;
    type: NotificationsConfigurationsType;
    body: string;
  }[];
  sectionLimit: number;
};

export type SEOPagesName =
  | 'landing'
  | 'search'
  | 'radio'
  | 'terms'
  | 'privacy'
  | 'podcasts'
  | 'last-news'
  | 'editorial'
  | 'contacts'
  | 'authors'
  | 'technical_sheet';

export type CreateSEOForm = {
  pages: {
    name: SEOPagesName;
    title?: string;
    description?: string;
    keywords?: string;
  }[];
};
